/** Lead status labels + transition helpers for Signals Change Status UI. */

export const LEAD_STATUS = {
  IN_PROGRESS: "in_progress",
  QUALIFIED_INQUIRY: "qualified_inquiry",
  QUALIFIED_MATURE: "qualified_mature",
  UNQUALIFIED_INQUIRY: "unqualified_inquiry",
  UNQUALIFIED_MATURE: "unqualified_mature",
};

export const LEAD_STATUS_OPTIONS = [
  {
    value: LEAD_STATUS.QUALIFIED_INQUIRY,
    label: "Qualified as Inquiry Lead",
    description: "Prospect replied; automated outreach stops and conversation continues manually.",
  },
  {
    value: LEAD_STATUS.QUALIFIED_MATURE,
    label: "Qualified as Mature Lead",
    description: "Qualified business opportunity for sales follow-up.",
  },
  {
    value: LEAD_STATUS.UNQUALIFIED_MATURE,
    label: "Unqualified as Mature Lead",
    description: "Prospect responded but will not proceed; close and exclude from further sales.",
  },
  {
    value: LEAD_STATUS.UNQUALIFIED_INQUIRY,
    label: "Unqualified as Inquiry Lead",
    description: "Outreach completed with no response.",
  },
];

const LABEL_BY_VALUE = {
  new: "In Progress",
  exited: "Exited",
  [LEAD_STATUS.IN_PROGRESS]: "In Progress",
  ...Object.fromEntries(LEAD_STATUS_OPTIONS.map((o) => [o.value, o.label])),
};

export function leadStatusLabel(value) {
  if (!value) return null;
  return LABEL_BY_VALUE[value] || value;
}

export function isUnqualifiedLeadStatus(value) {
  return value === LEAD_STATUS.UNQUALIFIED_INQUIRY
    || value === LEAD_STATUS.UNQUALIFIED_MATURE;
}

export function isQualifiedLeadStatus(value) {
  return value === LEAD_STATUS.QUALIFIED_INQUIRY
    || value === LEAD_STATUS.QUALIFIED_MATURE;
}

export function leadStatusColorClass(uiOrLeadStatus) {
  const s = String(uiOrLeadStatus || "");
  if (s.startsWith("Qualified") || isQualifiedLeadStatus(s)) return "green";
  if (s.startsWith("Unqualified") || isUnqualifiedLeadStatus(s)) return "red";
  if (s === "In Progress" || s === "in_progress" || s === "new") return "amber";
  return "gray";
}

/** Any status except In Progress — selectable regardless of current status. */
export function selectableLeadStatuses(_currentLeadStatus) {
  return LEAD_STATUS_OPTIONS;
}

export function canChangeLeadStatus(rnc) {
  return Boolean(rnc?.enrollmentId && rnc?.campaignId && rnc?.leadStatus);
}

export const LEAD_NOTIFICATION_INCOMPLETE_MESSAGE =
  "Please configure Lead Notification settings (Name, Email, and Email Template) in Settings → General before qualifying an opportunity.";

/** True when workspace Lead Notification name, email, and template are all set. */
export function isLeadNotificationConfigured(settings) {
  if (!settings || typeof settings !== "object") return false;
  const name = String(settings.lead_notification_name || "").trim();
  const email = String(settings.lead_notification_email || "").trim();
  const templateConfigured = Boolean(settings.lead_notification_template_configured)
    || Boolean(String(settings.lead_notification_email_template || "").trim());
  return Boolean(name && email && templateConfigured);
}

/**
 * Frontend gate before qualifying a lead.
 * Throws an Error with code ``lead_notification_incomplete`` when config is incomplete.
 */
export async function assertLeadNotificationConfigured() {
  const { generalSettingsApi } = await import("@/lib/api");
  const settings = await generalSettingsApi.get();
  if (!isLeadNotificationConfigured(settings)) {
    const err = new Error(LEAD_NOTIFICATION_INCOMPLETE_MESSAGE);
    err.code = "lead_notification_incomplete";
    throw err;
  }
  return settings;
}

/** Map API / gate errors for qualification toasts. */
export function leadStatusErrorMessage(err) {
  if (err?.code === "lead_notification_incomplete") {
    return err.message || LEAD_NOTIFICATION_INCOMPLETE_MESSAGE;
  }
  return err?.message || "Failed to update lead status";
}

/**
 * After a successful setEnrollmentLeadStatus call, toast status + email outcome.
 * Qualification can succeed while delivery fails — never imply the email was sent.
 */
export function toastLeadStatusUpdate(toast, result, lead_status) {
  const label = result?.label || leadStatusLabel(lead_status) || "updated";
  const notify = result?.ownerNotification;
  if (isQualifiedLeadStatus(lead_status) && notify && notify.sent === false && !notify.skipped) {
    toast.warning(
      notify.message
        || "Lead status updated, but the Lead Notification email could not be sent. Check Gmail or SMTP settings.",
    );
    return;
  }
  if (isQualifiedLeadStatus(lead_status) && notify?.sent) {
    toast.success(`Lead marked ${label}. Notification email sent.`);
    return;
  }
  toast.success(`Lead marked ${label}`);
}